import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { signOut } from "firebase/auth";
import { auth } from "@/data/firebase";
import { useAuth } from "@/data/AuthProvider";

const LINK = "font-mono text-xs font-medium tracking-[0.15em] uppercase hover:underline";

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const authState = useAuth();
  const user = authState.status === "signedIn" ? authState.user : null;

  async function handleSignOut() {
    await signOut(auth);
    // TanStack Query owns the trade cache and drops it when the key changes.
    // The 2020 handler had to clear a UserContext holding every trade by hand.
    void navigate("/");
  }

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Following a link changes the route but leaves the panel open, covering the
  // page you just navigated to.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    /*
      `relative` is load-bearing. The mobile panel below is `absolute
      inset-x-0 top-full`, and with no positioned ancestor those resolve
      against the initial containing block — a viewport-sized box at the
      document origin — so `top: 100%` rendered the menu a full viewport
      height down the page. The button toggled, the panel opened, and it was
      simply off the bottom of the screen.

      It belongs on <nav> rather than on the max-w-5xl container inside it, so
      the panel spans the full width and its bottom rule runs edge to edge.
    */
    <nav className="rule relative bg-paper" aria-label="Main">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
        <Link to="/" className="text-lg font-black tracking-tighter">
          TRADE-R
        </Link>

        <button
          type="button"
          className="cursor-pointer font-mono text-xs font-medium tracking-[0.2em] uppercase md:hidden"
          aria-expanded={mobileOpen}
          aria-controls="main-menu"
          onClick={() => setMobileOpen((o) => !o)}
        >
          {mobileOpen ? "Close" : "Menu"}
        </button>

        <div
          id="main-menu"
          className={`${
            mobileOpen ? "flex" : "hidden"
          } absolute inset-x-0 top-full z-20 flex-col gap-4 border-b-2 border-ink bg-paper px-6 py-5 md:static md:flex md:flex-row md:items-center md:gap-7 md:border-0 md:py-0`}
        >
          {user && (
            <>
              <Link
                to="/trade/list"
                className={`${LINK} ${isActive("/trade/list") ? "underline" : ""}`}
              >
                Trades
              </Link>
              <Link to="/dashboard" className={LINK}>
                Dashboard
              </Link>
            </>
          )}
          <Link to="/resources" className={LINK}>
            Resources
          </Link>

          {user ? (
            <div className="flex items-center gap-4 md:ml-3">
              <Link to="/settings" className={LINK}>
                Settings
              </Link>
              <button type="button" className={`${LINK} cursor-pointer`} onClick={() => void handleSignOut()}>
                Sign out
              </button>
              <Link to="/trade/form" className="btn-primary btn-sm">
                Add trade
              </Link>
            </div>
          ) : (
            <div className="flex items-center gap-3 md:ml-3">
              <Link className={LINK} to="/user/sign-in">
                Sign in
              </Link>
              <Link className="btn-accent btn-sm" to="/user/start">
                Get started
              </Link>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
